import { useAlert } from '@/common/alert/AlertContext';
import { Form } from '@/common/form/Form';
import { FormFields } from '@/common/form/rendering/FormFields';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * End-dates the selected Customer Relationships rather than deleting them, so the history of who was
 * related to whom stays intact for prior periods.
 */
export const CustRelExpireDialog = ({ custRels, module, closeDialog }) => {
  const rsrc = useRsrc();
  const { showAlert } = useAlert();
  const [endDateFieldDefs, setEndDateFieldDefs] = useState();
  const [formValues, setFormValues] = useState({ endDate: DateUtils.today() });
  const [saving, setSaving] = useState(false);
  const formRef = useRef();

  // An end date before this would invert a selected relationship's own date range
  const latestStartDate = useMemo(
    () => custRels.reduce((latest, custRel) => Math.max(latest, Number(custRel.startDate) || 0), 0),
    [custRels],
  );

  useEffect(() => {
    rsrc.fieldDefs(module, 'CUST_REL').then((fieldDefs) => {
      setEndDateFieldDefs(fieldDefs.filter((fieldDef) => fieldDef.field === 'endDate'));
    });
  }, []);

  const submit = () => {
    if (formRef.current?.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }

    const endDate = formValues.endDate;
    if (!endDate) {
      showAlert('Choose an end date.', 'warning');
      return;
    }

    if (Number(endDate) < latestStartDate) {
      const tooLate = custRels.filter((custRel) => Number(custRel.startDate) > Number(endDate)).length;
      showAlert(
        `${tooLate} of the selected relationship${tooLate === 1 ? '' : 's'} start after ` +
          `${DateUtils.format(Number(endDate))}. Choose an end date on or after ${DateUtils.format(latestStartDate)}.`,
        'warning',
      );
      return;
    }

    setSaving(true);

    const patchObjs = custRels.map((custRel) => ({ id: custRel.id, endDate }));
    rsrc
      .save(module, 'CUST_REL', patchObjs)
      .then(() => closeDialog(true))
      .catch((error) => {
        setSaving(false);
        showAlert(error?.response?.data?.message ?? 'The relationships could not be expired.', 'error');
      });
  };

  return (
    <Dialog open fullWidth onClose={() => closeDialog()} data-test-id="cust-rel-expire-dialog">
      <DialogTitle>Expire Relationships</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ marginBottom: '1rem' }}>
          {custRels.length === 1 ?
            'The selected relationship will be given this end date.'
          : `The ${custRels.length} selected relationships will be given this end date.`}
          {latestStartDate > 0 && ` The latest start date in the selection is ${DateUtils.format(latestStartDate)}.`}
        </DialogContentText>
        <Form ref={formRef} formValues={formValues} setFormValues={setFormValues}>
          <FormFields fieldDefs={endDateFieldDefs} />
        </Form>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={submit}
          disabled={saving || !endDateFieldDefs}
          data-test-id="cust-rel-expire-submit"
        >
          Expire
        </Button>
      </DialogActions>
    </Dialog>
  );
};
